import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, User, LogOut, MessageSquare, Settings, Globe, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout, isAuthenticated, isProfessional } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notif, setNotif] = useState({ unread_messages: 0, recent_reviews: 0 });

  const fetchCounts = async () => {
    try {
      const res = await axios.get(`${API}/notifications/counts`);
      setNotif(res.data);
    } catch (_e) {
      // silent fail: notifications are non-critical
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !isProfessional) {
      setNotif({ unread_messages: 0, recent_reviews: 0 });
      return;
    }
    fetchCounts();
    const intervalId = setInterval(fetchCounts, 30000); // poll every 30s
    return () => clearInterval(intervalId);
  }, [isAuthenticated, isProfessional]);

  // Refetch on route changes (e.g. after opening messages/dashboard)
  useEffect(() => {
    if (isAuthenticated && isProfessional) {
      fetchCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const languages = [
    { code: 'it', label: 'Italiano' },
    { code: 'en', label: 'English' },
    { code: 'pl', label: 'Polski' },
    { code: 'de', label: 'Deutsch' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
              <span className="text-orange-500 font-bold text-sm">A</span>
            </div>
            <span className="font-semibold text-slate-900 text-lg tracking-tight" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              AUTOMAT.IO
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-home"
            >
              {t('nav_home')}
            </Link>
            <Link 
              to="/directory" 
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-directory"
            >
              {t('nav_directory')}
            </Link>
            <a
              href="/#contact"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="nav-contact"
            >
              {t('contact_badge')}
            </a>
            
            {isAuthenticated ? (
              <>
                {isProfessional && (
                  <Link 
                    to="/messages" 
                    className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="nav-messages"
                  >
                    {t('nav_messages')}
                    {notif.unread_messages > 0 && (
                      <span
                        className="absolute -top-2 -right-4 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md"
                        data-testid="nav-messages-badge"
                      >
                        {notif.unread_messages > 9 ? '9+' : notif.unread_messages}
                      </span>
                    )}
                  </Link>
                )}
                <Link 
                  to="/dashboard" 
                  className="relative text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="nav-dashboard"
                >
                  {t('nav_dashboard')}
                  {isProfessional && notif.recent_reviews > 0 && (
                    <span
                      className="absolute -top-2 -right-4 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md"
                      data-testid="nav-dashboard-badge"
                    >
                      {notif.recent_reviews > 9 ? '9+' : notif.recent_reviews}
                    </span>
                  )}
                </Link>
              </>
            ) : null}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-1 border-slate-300 hover:border-orange-500" 
                  data-testid="language-switcher"
                >
                  <Globe className="h-4 w-4 text-slate-600" />
                  <span className="uppercase text-xs font-medium">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? 'bg-orange-50 text-orange-600' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 relative" data-testid="user-menu">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline text-sm">{user?.name}</span>
                    {isProfessional && notif.total > 0 && (
                      <span
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white"
                        data-testid="user-menu-dot"
                      />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <Settings className="h-4 w-4 mr-2" />
                    <span className="flex-1">{t('nav_dashboard')}</span>
                    {isProfessional && notif.recent_reviews > 0 && (
                      <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                        {notif.recent_reviews > 9 ? '9+' : notif.recent_reviews}
                      </span>
                    )}
                  </DropdownMenuItem>
                  {isProfessional && (
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      <span className="flex-1">{t('nav_messages')}</span>
                      {notif.unread_messages > 0 && (
                        <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {notif.unread_messages > 9 ? '9+' : notif.unread_messages}
                        </span>
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/subscription')} data-testid="menu-subscription">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t('nav_subscription') || 'Abbonamento'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-btn"
                >
                  {t('nav_login')}
                </Button>
                <Button 
                  size="sm"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  onClick={() => navigate('/register')}
                  data-testid="nav-register-btn"
                >
                  {t('nav_register')}
                </Button>
              </div>
            )}

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm" data-testid="mobile-menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/')}>
                  {t('nav_home')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/directory')}>
                  {t('nav_directory')}
                </DropdownMenuItem>
                {isAuthenticated && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      {t('nav_dashboard')}
                    </DropdownMenuItem>
                    {isProfessional && (
                      <DropdownMenuItem onClick={() => navigate('/messages')}>
                        {t('nav_messages')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/subscription')}>
                      {t('nav_subscription') || 'Abbonamento'}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
